import React, { useEffect, useState, Suspense, lazy } from 'react';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { logVisit, logInteraction } from './utils/firebaseAnalytics';
import AdminPanel from './components/AdminPanel';
import './App.css';


// Lazy load sections for ultra-fast initial paint
const About = lazy(() => import('./components/About'));
const Projects = lazy(() => import('./components/Projects'));
const Certifications = lazy(() => import('./components/Certifications'));
const Blogs = lazy(() => import('./components/Blogs'));
const Contact = lazy(() => import('./components/Contact'));



// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Normalize scroll for ultra-consistency
ScrollTrigger.normalizeScroll(true);

const LovePopup = ({ onClose }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showSad, setShowSad] = useState(false);
  const popupRef = React.useRef(null);
  
  useEffect(() => {
    let sadTimer;
    if (!showSad) {
      sadTimer = setTimeout(() => {
        setShowSad(true);
        logInteraction('popup_ignored_timeout');
        setTimeout(onClose, 2000); 
      }, 15000); 
    }
    return () => clearTimeout(sadTimer);
  }, [showSad, onClose]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        if (!showSad) {
          setShowSad(true);
          logInteraction('popup_ignored_click_outside');
          setTimeout(onClose, 2000);
        }
      }
    };
    
    // Add small delay before attaching so that the initial click doesn't trigger it
    const clickTimer = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick);
    }, 500);
    
    return () => {
      clearTimeout(clickTimer);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showSad, onClose]);

  const handleOptionClick = (option) => {
    logInteraction('popup_clicked', { option });
    onClose();
  };

  return (
    <motion.div
      ref={popupRef}
      key="love-popup"
      className="curved-popup"
      initial={{ opacity: 0, y: 50, scale: 0.9, rotate: -5 }}
      animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 100, damping: 15 }}
      onMouseEnter={() => setShowOptions(true)}
      onClick={() => setShowOptions(true)}
      onTouchStart={() => setShowOptions(true)}
    >
      <div className="curved-content">
        {showSad ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span className="curved-text">Alright bro</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="40" 
              height="40" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#6c5ce7" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ marginTop: '10px' }}
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M16 16s-1.5-2-4-2-4 2-4 2"></path>
              <line x1="9" y1="9" x2="9.01" y2="9"></line>
              <line x1="15" y1="9" x2="15.01" y2="9"></line>
            </svg>
          </div>
        ) : showOptions ? (
          <>
            <span className="curved-text" style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Let us know!</span>
            <div className="popup-options">
              <button className="popup-option-btn primary" onClick={(e) => { e.stopPropagation(); handleOptionClick('Yupp loved it'); }}>Yupp loved it</button>
              <button className="popup-option-btn secondary" onClick={(e) => { e.stopPropagation(); handleOptionClick('Totally loved it'); }}>Totally loved it</button>
            </div>
          </>
        ) : (
          <>
            <span className="curved-text">Hey, still here!</span>
            <span className="curved-text-sub">You loved it, right?</span>
          </>
        )}
      </div>

      {!showSad && (
        <button 
          className="curved-close"
          onClick={(e) => {
            e.stopPropagation();
            setShowSad(true);
            logInteraction('popup_closed_manually');
            setTimeout(onClose, 2000);
          }}
        >
          ×
        </button>
      )}
    </motion.div>
  );
};

const HomeLayout = ({ hasEntered, handleEnterSite, showPopup, setShowPopup, currentSection }) => {
  return (
    <>
      <AnimatePresence>
        {!hasEntered && (
          <motion.div 
            className="welcome-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              zIndex: 99999,
              background: '#0a0a0a', // Deep dark background
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={handleEnterSite}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1 }}
            >
               <h1 style={{ 
                 color: '#ffffff', 
                 fontSize: '2rem', 
                 fontFamily: "'Outfit', sans-serif",
                 fontWeight: 300,
                 letterSpacing: '5px',
                 textTransform: 'uppercase'
               }}>
                 Rahul Mishra
               </h1>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 1, duration: 1, repeat: Infinity, repeatType: "reverse" }}
              style={{
                marginTop: '2rem',
                color: '#6c5ce7',
                fontSize: '0.9rem',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '2px'
              }}
            >
              CLICK TO ENTER
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Navbar />
      <main className="main-content">
        <section id="home">
          <Home />
        </section>
        
        <Suspense fallback={<div style={{ height: '30vh', background: '#000000' }} />}>
          <section id="about">
            <About />
          </section>
          <section id="projects">
            <Projects />
          </section>
          <section id="certifications">
            <Certifications />
          </section>
          <section id="blogs">
            <Blogs />
          </section>
          <section id="contact">
            <Contact />
          </section>
        </Suspense>
      </main>

      <AnimatePresence>
        {showPopup && hasEntered && currentSection === 'home' && (
          <LovePopup onClose={() => setShowPopup(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

const App = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [currentSection, setCurrentSection] = useState('home');

  useEffect(() => {
    // Show popup after 3 seconds
    const timer = setTimeout(() => setShowPopup(true), 10000);

    // Track current section based on scroll position
    const handleScroll = () => {
      const sections = ['home', 'about', 'projects', 'certifications', 'blogs', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setCurrentSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    // Initialize Lenis for "Weightless Butter" effect
    const lenis = new Lenis({
      lerp: 0.05,
      wheelMultiplier: 1.1,
      touchMultiplier: 2,
      smoothWheel: true,
      infinite: false,
    });

    // Expose lenis to window for use in other components
    window.lenis = lenis;

    // Connect Lenis to ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    // Handle all anchor links
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a');
      if (target && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href').substring(1);
        const element = document.getElementById(id);
        if (element) {
          lenis.scrollTo(element, { offset: -80 });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    const updateRaf = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(updateRaf);
    gsap.ticker.lagSmoothing(0);

    // 5. Intelligent Preloading: Load other sections after 1.5s to keep initial paint clear
    const preloadTimer = setTimeout(() => {
      import('./components/About');
      import('./components/Projects');
      import('./components/Contact');
    }, 1500);

    // 6. Auto-Fullscreen on First Interaction
    const triggerFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          // Silent catch if it fails (e.g. strict browser policy)
          console.log("Auto-fullscreen requires stronger gesture", err);
        });
      }
      // Remove listener immediately after first attempt to avoid annoyance
      document.removeEventListener('click', triggerFullscreen);
      document.removeEventListener('keydown', triggerFullscreen);
      document.removeEventListener('touchstart', triggerFullscreen);
    };

    // Listen for any interaction to trigger fullscreen "by default" (as soon as possible)
    document.addEventListener('click', triggerFullscreen);
    document.addEventListener('keydown', triggerFullscreen);
    document.addEventListener('touchstart', triggerFullscreen);

    // Clean up
    return () => {
      clearTimeout(timer);
      clearTimeout(preloadTimer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorClick);
      document.removeEventListener('click', triggerFullscreen);
      document.removeEventListener('keydown', triggerFullscreen);
      document.removeEventListener('touchstart', triggerFullscreen);
      gsap.ticker.remove(updateRaf);
      lenis.destroy();
    };


  }, []);


  // Check localStorage for "hasEntered" state to skip intro on reload
  const [hasEntered, setHasEntered] = useState(() => {
    return localStorage.getItem('hasEntered') === 'true';
  });

  useEffect(() => {
    if (hasEntered) {
      logVisit();
    }
  }, [hasEntered]);

  // ... (keep existing effects)

  const handleEnterSite = () => {
    // 1. Enter Fullscreen
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log("Full screen denied:", err);
      });
    }
    // 2. Dismiss Overlay and persist state
    setHasEntered(true);
    localStorage.setItem('hasEntered', 'true');
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="app-container">
          <Routes>
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/" element={
              <HomeLayout 
                hasEntered={hasEntered} 
                handleEnterSite={handleEnterSite}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                currentSection={currentSection}
              />
            } />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
